import React, { useState } from 'react';
import { Box, Typography, Container, Button, Grid, useMediaQuery, useTheme, IconButton, Drawer, Pagination } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import NavBar from '~/components/NavBar';
import Footer from '~/components/Footer';
import SideBar from '~/components/SideBar';
import ActionCard from "~/components/ActionManagement/Card";
import ActionManagementsClient from '../api/ActionManagementsClient';
import { useApiCall } from '~/single-page/hooks/useApiCall';
import { APP_ROUTES } from '~/single-page/constants/routes';
// import { useRailsContext } from '~/single-page/contexts/RailsContext';
import SearchField from '~/components/SearchField';
import { useNormalizedSearch } from '~/single-page/hooks/useNormalizedSearch';
import NoItemsMessage from "~/components/NoItemsMessage";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import NGOsMapNew from "~/components/NGOs/MapNew";



const getItemsPerPage = (isMobile, isTablet) => {
  if (isMobile) return 4;     
  if (isTablet) return 6;     
  return 9;                   
};

// Componente do Banner Principal
const HeroBanner = ({ searchTerm, setSearchTerm, setNormalizedSearchTerm, setPage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:480px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  
  // Breakpoints otimizados para melhor responsividade
  const isSmallTablet = useMediaQuery('(min-width:481px) and (max-width:600px)');
  const isMediumTablet = useMediaQuery('(min-width:601px) and (max-width:900px)');
  const isLargeTablet = useMediaQuery('(min-width:901px) and (max-width:1200px)');
  const isSmallDesktop = useMediaQuery('(min-width:1201px) and (max-width:1351px)');
  const isLargeDesktop = useMediaQuery('(min-width:1352px)');
  
  // Função para determinar configurações da imagem baseado nos breakpoints
  const getImageDimensions = () => {
    if (isSmallTablet) {
      return { 
        width: '100%', 
        minHeight: '600px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isMediumTablet) {
      return { 
        width: '100%', 
        minHeight: '650px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isLargeTablet) {
      return { 
        width: '100%', 
        minHeight: '700px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isSmallDesktop) {
      return { 
        width: '100%', 
        minHeight: '750px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      };
    }
    if (isLargeDesktop) {
      return { 
        width: '100%', 
        minHeight: '710px',
        marginBottom: '0px',
        backgroundSize: 'cover',
        backgroundPosition: 'center -41px'
      };
    }
    return { 
      width: '100%', 
      minHeight: '700px',
      marginBottom: '0px',
      backgroundSize: 'cover',
      backgroundPosition: 'center -80px'
    };
  };

  const imageDimensions = getImageDimensions();
  
  if (isMobile) {
    return (
      <>
        {/* Banner Mobile com conteúdo sobreposto */}
        <Box
          sx={{
            width: '100%',
            minHeight: '400px',
            marginTop: '16px',
            backgroundImage: 'url("/images/main-banner/home_moving_actions.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            marginBottom: '0px', // Garantir que não há margin negativo
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }
          }}
        >
          {/* Conteúdo sobreposto - Mobile */}
          <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2, px: 3, pt: 10 }}>
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              {/* Logo */}
              <Box
                component="img"
                src="/images/moving_actions/bora_impactar.png"
                alt="Bora Impactar"
                sx={{
                  maxWidth: '200px',
                  width: '100%',
                  height: 'auto',
                  mb: 3
                }}
              />
              
              {/* Título */}
              <Typography
                variant="h4"
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mb: 2,
                  color: 'white'
                }}
              >
                Busque uma ação e transforme o mundo
              </Typography>
              
              {/* Campo de pesquisa */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar ações, projetos ou ongs..."
                  value={searchTerm}
                  onChange={(e) => { 
                    setSearchTerm(e.target.value);
                    setNormalizedSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      '& fieldset': { border: 'none' },
                      fontSize: '0.9rem'
                    }
                  }}
                />
              </Box>
              
              {/* Texto descritivo */}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  opacity: 0.9,
                  color: 'white'
                }}
              >
                Procure por nome da ação, descrição, projeto ou ONG
              </Typography>
            </Box>
          </Container>

          {/* Decoração curva azul - Mobile */}
          <Box
            component="img"
            src="/images/sponsors_companies/blue-curve-decorator.png"
            alt="Decoração curva azul"
            sx={{
              position: 'absolute',
              left: '-1px',
              top: '107%',
              transform: 'translateY(-50%)',
              width: '38px',
              height: '103px',
              zIndex: 10,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />
        </Box>
      </>
    );
  }
  
  // Layout desktop/tablet
  return (
    <Box
      sx={{
        width: imageDimensions.width,
        minHeight: imageDimensions.minHeight,
        marginTop: imageDimensions.marginTop,
        marginBottom: imageDimensions.marginBottom,
        backgroundImage: 'url("/images/main-banner/home_moving_actions.png")',
        backgroundSize: imageDimensions.backgroundSize || 'cover',
        backgroundPosition: imageDimensions.backgroundPosition || 'center -99px',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        mx: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1
        }
      }}
    >
      {/* Conteúdo sobreposto - Desktop/Tablet */}
      <Container maxWidth="md" sx={{ 
        position: 'relative', 
        zIndex: 2, 
        px: 4, 
        py: { xs: 4, sm: 6, md: 8 }, 
        pb: { xs: 4, sm: 6, md: 0 },
        mt: isSmallTablet ? 6 : isMediumTablet ? 8 : isLargeTablet ? 12 : isSmallDesktop ? 14 : { xs: 4, sm: 6, md: 15.625 }
      }}>
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          {/* Logo */}
          <Box
            component="img"
            src="/images/moving_actions/bora_impactar.png"
            alt="Bora Impactar"
            sx={{
              maxWidth: { sm: '300px', md: '400px' },
              width: '100%',
              height: 'auto',
              mb: 4
            }}
          />
          
          {/* Título */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { sm: '2rem', md: '2.5rem', lg: '2.4rem' },
              fontWeight: 'bold',
              mb: 4,
              color: 'white',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Busque uma ação e transforme o mundo
          </Typography>
          
          {/* Campo de pesquisa */}
          <Box sx={{ mb: 3, maxWidth: '950px', mx: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Pesquisar ações, projetos ou ongs..."
              value={searchTerm}
              onChange={(e) => { 
                setSearchTerm(e.target.value);
                setNormalizedSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999', fontSize: '2.3rem' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '3px 15px',
                  fontSize: { sm: '1rem', md: '1.1rem' },
                  '& fieldset': { border: 'none' },
                  '& input': {
                    padding: '12px 0'
                  }
                }
              }}
            />
          </Box>
          
          {/* Texto descritivo */}
          <Typography
            variant="body1"
            sx={{
              fontSize: { sm: '1rem', md: '1.3rem' },
              opacity: 0.9,
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            Procure por nome da ação, descrição, projeto ou ONG
          </Typography>
        </Box>
      </Container>

      {/* Decoração curva branca - Desktop/Tablet */}
      {(isLargeTablet || isSmallDesktop || isLargeDesktop) && (
        <Box
          component="img"
          src="/images/moving_actions/white_curve_decorator.png"
          alt="Decoração curva branca"
          sx={{
            display: { xs: 'none', sm: isSmallTablet || isMediumTablet ? 'none' : 'block' },
            position: 'absolute',
            left: 0,
            top: '38%',
            transform: 'translateY(-50%)',
            width: '100px',
            height: '320px',
            zIndex: 10,
            opacity: 0.9,
            pointerEvents: 'none'
          }}
        />
      )}

      
    </Box>
  );
};



// Componente da Seção de Ações
const ActionsSection = ({ searchTerm, filteredActions, page, setPage }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:600px)');
    const isTablet = useMediaQuery('(max-width:900px)');
  
    const itemsPerPage = getItemsPerPage(isMobile, isTablet);
    const totalPages = Math.ceil(filteredActions.length / itemsPerPage);
    const paginatedActions = filteredActions.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  
    const handlePageChange = (event, value) => {
      setPage(value);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  
    return (
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          width: '100%',
          pt: { xs: 4, sm: 5, md: 6 },
          pb: { xs: 6, sm: 8, md: 10 },
          position: 'relative'
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 10 } }}>
          {/* Título da seção */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 6, lg: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                fontSize: { 
                  xs: '1.3rem',  // 460px e menor
                  sm: '1.6rem',  // 600px+
                  md: '1.9rem',  // 900px+
                  lg: '2.2rem'   // 1200px+
                },
                color: '#2E2F32',
                mb: { xs: 1.5, sm: 2, md: 2.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: { xs: 0.5, sm: 1 },
                lineHeight: { xs: 1.2, sm: 1.3 }
              }}
            >

              {/* Logo ao lado de "Ações" */}
              <Box
                component="img"
                src="/images/moving_actions/bora_impactar2.png"
                alt="Logo Bora Impactar"
                sx={{
                  width: { 
                    xs: '24px',  // 460px e menor
                    sm: '30px',  // 600px+
                    md: '40px',  // 900px+
                    lg: '52px'   // 1200px+
                  },
                  height: 'auto',
                  marginTop: { xs: '-12px', sm: '-14px', md: '-16px' },
                  flexShrink: 0
                }}
              />
              <Box
                component="span"
                sx={{
                  color: '#009FE3',
                  position: 'relative',
                  display: 'inline-block',
                  minWidth: 'fit-content'
                }}
              >
                Ações
                <Box
                  component="img"
                  src="/images/moving_actions/blue_underline.png"
                  alt="Underline decorativo"
                  sx={{
                    position: 'absolute',
                    bottom: { xs: '-8px', sm: '-10px' },
                    left: { xs: '-20px', sm: '-25px', md: '-29px' },
                    width: { xs: '130%', sm: '140%', md: '150%' },
                    height: 'auto',
                    maxHeight: { xs: '10px', sm: '11px', md: '13px' },
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Box component="span" sx={{ whiteSpace: { xs: 'nowrap', sm: 'normal' } }}>
                em movimento
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#000000',
                opacity: 0.9,
                fontSize: { 
                  xs: '0.85rem',  // 460px e menor
                  sm: '0.95rem',  // 600px+
                  md: '1.05rem',  // 900px+
                  lg: '1.1rem'    // 1200px+
                },
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                maxWidth: { xs: '320px', sm: '500px', md: '700px', lg: '800px' },
                mx: 'auto',
                textAlign: 'center',
                px: { xs: 1, sm: 2 }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'block', md: 'inline' } }}>
                Fique por dentro! Confira as ações que estão rolando na cidade.
              </Box>
              {' '}
              <Box 
                component="span" 
                sx={{ 
                  display: { xs: 'block', md: 'inline' },
                  mt: { xs: 0.5, md: 0 }
                }}
              >
                <br sx={{ display: { xs: 'none', md: 'block' } }} />
                Uma delas pode se encaixar no seu perfil e você pode impactar a vida de muita gente!
              </Box>
            </Typography>
          </Box>
  
          {/* Decoração curva laranja */}
          <Box
            component="img"
            src="/images/moving_actions/orange_curve_decorator.png"
            alt="Decoração curva laranja"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              left: -1,
              top: '1%',
              width: '69px',
              height: '543px',
              zIndex: 0,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />
  
          {/* Grid de ações - layout responsivo */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {paginatedActions.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',                    // Mobile: 1 coluna
                      sm: 'repeat(2, 1fr)',        // Tablet pequeno: 2 colunas
                      md: 'repeat(3, 1fr)'         // Desktop: 3 colunas
                    },
                    gap: { xs: 3, sm: 3, md: 4 }, // Espaçamento responsivo entre cards
                    justifyItems: 'center',
                    alignItems: 'stretch',
                    width: '100%',
                    maxWidth: '100%'
                  }}
                >
                  {paginatedActions.map((action, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        width: '100%',
                        maxWidth: {
                          xs: '100%',        // Mobile: largura total
                          sm: '400px',       // Tablet: máximo 400px
                          md: '450px'        // Desktop: máximo 450px para ficar mais largo
                        },
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      <ActionCard 
                        action={action} 
                        sx={{ 
                          width: '100%',
                          minHeight: { xs: '320px', sm: '340px', md: '360px' },
                          maxHeight: { xs: '400px', sm: '380px', md: '400px' },
                          display: 'flex',
                          flexDirection: 'column',
                          // Garantir que o card tenha espaço suficiente para os botões
                          '& .MuiCardContent-root': {
                            flex: '1 0 auto',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          },
                          // Estilo específico para a área dos botões
                          '& .action-buttons': {
                            marginTop: 'auto',
                            paddingTop: 2
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
  
                {/* Navegação com setas e traços */}
                {filteredActions.length > itemsPerPage && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    mt: { xs: 6, sm: 8 },
                    gap: 1,
                    alignItems: 'center'
                  }}>
                    {/* Seta esquerda */}
                    <IconButton
                      onClick={() => handlePageChange(null, page - 1)}
                      disabled={page <= 1}
                      sx={{
                        color: '#3D3D3D',
                        padding: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        },
                        '&:disabled': {
                          opacity: 0.3
                        }
                      }}
                    >
                      <ArrowBackIosIcon sx={{ fontSize: '20px' }} />
                    </IconButton>

                    {/* Traço esquerdo */}
                    <Box sx={{
                      width: '16px',
                      height: '2px',
                      backgroundColor: '#3D3D3D',
                      mr: 1,
                      ml: -3.3,
                      opacity: page <= 1 ? 0.1 : 1
                    }} />

                    {/* Paginação padrão no centro */}
                    <Pagination 
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      siblingCount={1}
                      boundaryCount={1}
                      hidePrevButton={true}
                      hideNextButton={true}
                      sx={{
                        '& .MuiPagination-ul': {
                          gap: 0.5
                        },
                        '& .MuiPaginationItem-root': {
                          fontSize: '1rem',
                          minWidth: '32px',
                          height: '32px'
                        }
                      }}
                    />

                    {/* Traço direito */}
                    <Box sx={{
                      width: '16px',
                      height: '2px',
                      backgroundColor: '#3D3D3D',
                      ml: 1,
                      mr: -2.6,
                      opacity: page >= totalPages ? 0.1 : 1
                    }} />

                    {/* Seta direita */}
                    <IconButton
                      onClick={() => handlePageChange(null, page + 1)}
                      disabled={page >= totalPages}
                      sx={{
                        color: '#3D3D3D',
                        padding: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        },
                        '&:disabled': {
                          opacity: 0.3
                        }
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ 
                width: '100%', 
                textAlign: 'center', 
                py: 8 
              }}>
                <Typography color="text.secondary" variant="h6">
                  {searchTerm ? 
                    `Nenhuma ação encontrada com o termo "${searchTerm}"` :
                    'Nenhuma ação disponível no momento'
                  }
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    );
  };



// Componente principal da página
export default function MovingActionsPage() {
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [isClosing, setIsClosing] = React.useState(false);
    const isMobile = useMediaQuery('(max-width:700px)');
    
    // Estados de pesquisa
    const [searchTerm, setSearchTerm] = useState('');
    const [normalizedSearchTerm, setNormalizedSearchTerm] = useState('');
    const [page, setPage] = useState(1);
  
    const {
      data: actions = [],
      pending: loading,
      success,
    } = useApiCall({
      apiCall: ActionManagementsClient.actionManagements,
      init: true,
    });
  
    const filteredActions = useNormalizedSearch(actions, normalizedSearchTerm, [
      'title',
      'short_description',
      'description',
      'ong.name'
    ]);
    
    const handleDrawerClose = () => {
      setIsClosing(true);
      setMobileOpen(false);
    };
  
    const handleDrawerTransitionEnd = () => {
      setIsClosing(false);
    };
  
    const handleDrawerToggle = () => {
      if (!isClosing) {
        setMobileOpen(!mobileOpen);
      }
    };
    
    React.useEffect(() => {
      const originalBodyStyle = document.body.style.cssText;
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.backgroundColor = '#f5f5f5';
      
      return () => {
        document.body.style.cssText = originalBodyStyle;
      };
    }, []);
  
    if (loading) {
      return (
        <Box 
          sx={{ 
            width: '100vw', 
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Carregando ações...
          </Typography>
        </Box>
      );
    }
    
    if (!actions || actions.length === 0) {
      return (
        <Box 
          sx={{ 
            width: '100vw', 
            minHeight: '100vh',
            margin: 0, 
            padding: 0,
            backgroundColor: '#f5f5f5',
            position: 'relative',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <NavBar toggleDrawer={handleDrawerToggle} />
          
          <Box sx={{ mt: { xs: '70px', sm: '80px', md: '88px' } }}>
            <HeroBanner 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setNormalizedSearchTerm={setNormalizedSearchTerm}
              setPage={setPage}
            />
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                width: '100%',
                py: { xs: 6, sm: 8, md: 10 }
              }}
            >
              <Container maxWidth="lg">
                <NoItemsMessage itemName="ação" gender="feminino" />
              </Container>
            </Box>
          </Box>
          <Footer />
        </Box>
      );
    }
  
    return (
      <Box 
        sx={{ 
          width: '100vw', 
          minHeight: '100vh',
          margin: 0, 
          padding: 0,
          backgroundColor: '#f5f5f5',
          position: 'relative',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <NavBar toggleDrawer={handleDrawerToggle} />
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          onTransitionEnd={handleDrawerTransitionEnd}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: isMobile ? "block" : "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "100%",
              px: 3,
              pt: 2,
              position: 'relative',
              backgroundColor: (theme) => theme.palette.background.default,
            },
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            mb: 2
          }}>
            <IconButton 
              onClick={handleDrawerClose}
              sx={{ color: 'text.primary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <SideBar drawerToggle={handleDrawerClose} />
        </Drawer>
        
        <Box sx={{ mt: { xs: '70px', sm: '80px', md: '88px' } }}>
          <HeroBanner 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setNormalizedSearchTerm={setNormalizedSearchTerm}
            setPage={setPage}
          />
          <ActionsSection 
            searchTerm={searchTerm}
            filteredActions={filteredActions}
            page={page}
            setPage={setPage}
          />
        </Box>
        {/* Seção do Mapa com decoração laranja */}
        <Box 
          sx={{ 
            position: 'relative', 
            backgroundColor: '#f5f5f5', 
            py: 0
          }}
        >
          {/* Decoração curva laranja - Superior direita */}
          <Box
            component="img"
            src="/images/moving_actions/orange_curve_decorator2.png"
            alt="Decoração curva laranja"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              right: 0,
              top: '-5%',
              width: '180px',
              height: '200px',
              zIndex: 1,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />
          
          {/* Componente do mapa */}
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <NGOsMapNew filteredActions={filteredActions} searchTerm={searchTerm} />
          </Box>
        </Box>
        <Footer />
      </Box>
    );
}